/**
 * Fugas Meta — contactos Lead Ads, leads Kommo del período y cruce.
 * @module-isolated analytics-leads-recuperar-campana
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KpiGrid, LoadingState, SectionHeader, PageHeader, FilterBar, DateRangeFilter } from '../../../shared/components/index.js';
import {
  fetchKommoLeadsInRange,
  fetchMetaLeadAdsContacts,
  listMetaCsvKommoPipelines,
  matchMetaLeadsCsv,
  pushMetaLeadsToKommo
} from '../../../services/analytics-api.js';
import { getIdToken } from '../../../services/firebase-auth.js';
import { useLeadsData } from '../hooks/useLeadsData.jsx';
import { parseMetaLeadAdsCsv, readCsvFileText } from '../utils/meta-csv-parse.js';
import { cacheGet, cacheSet } from '../../../shared/utils/session-cache.js';
import { useAnalyticsRefresh } from '../../../shared/hooks/useAnalyticsRefresh.js';

const CSV_MATCH_BATCH = 8;
const CSV_PUSH_BATCH = 5;
const DEFAULT_CSV_KOMMO_TAGS = ['Meta Lead Ads'];
const CSV_KOMMO_SOURCE_NAME = 'GICapital-Importar';

function normalizeCsvTagName(raw) {
  return String(raw == null ? '' : raw)
    .trim()
    .substring(0, 80);
}

function csvEscape(v) {
  const s = String(v == null ? '' : v);
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

function downloadCsv(filename, headers, rows) {
  const lines = [headers.map(csvEscape).join(',')];
  rows.forEach((row) => {
    lines.push(headers.map((h) => csvEscape(row[h])).join(','));
  });
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtDate(s) {
  if (!s) return '—';
  const t = String(s);
  if (t.length >= 10) return t.slice(0, 10);
  return t;
}

function fmtMoney(n) {
  if (n == null || n === '' || !isFinite(Number(n))) return '—';
  return Number(n).toLocaleString('es-CL', { maximumFractionDigits: 0 });
}

function phoneKey(s) {
  const d = String(s == null ? '' : s).replace(/\D+/g, '');
  if (d.length < 8) return '';
  return d.slice(-9);
}

function emailKey(s) {
  return String(s == null ? '' : s)
    .trim()
    .toLowerCase();
}

function fugasCacheKey(from, to) {
  return 'fugas-meta-v6:' + String(from || '') + '|' + String(to || '');
}

function concatInstantRows(a, b) {
  const seenId = {};
  const seenP = {};
  const seenE = {};
  const out = [];
  function add(r) {
    if (!r) return;
    const row = Object.assign({}, r, { source: r.source || 'lead_ads' });
    const mid = String(row.metaLeadId || '').trim();
    const pk = phoneKey(row.telefono);
    const em = emailKey(row.email);
    if (mid && seenId[mid]) return;
    if (pk && seenP[pk]) return;
    if (em && em.indexOf('@') > 0 && seenE[em]) return;
    if (mid) seenId[mid] = true;
    if (pk) seenP[pk] = true;
    if (em) seenE[em] = true;
    out.push(row);
  }
  (Array.isArray(a) ? a : []).forEach(add);
  (Array.isArray(b) ? b : []).forEach(add);
  return out;
}

function metaOrigenLabel(row) {
  return row && row.fromCsv ? 'Meta CSV' : 'Meta API';
}

function mergeMetaContactRows(instantRows, webRows, kommoLeads) {
  const instant = Array.isArray(instantRows) ? instantRows : [];
  const web = Array.isArray(webRows) ? webRows : [];
  const kommo = Array.isArray(kommoLeads) ? kommoLeads : [];
  const kPhones = {};
  const kEmails = {};
  kommo.forEach((k) => {
    const pk = phoneKey(k.telefono);
    if (pk) kPhones[pk] = true;
    const em = emailKey(k.email);
    if (em && em.indexOf('@') > 0) kEmails[em] = true;
  });
  const phones = {};
  const emails = {};
  const out = [];
  instant.forEach((r) => {
    const row = Object.assign({}, r, { source: r.source || 'lead_ads' });
    out.push(row);
    const pk = phoneKey(row.telefono);
    if (pk) phones[pk] = true;
    const em = emailKey(row.email);
    if (em) emails[em] = true;
  });
  web.forEach((r) => {
    const pk = phoneKey(r.telefono);
    const em = emailKey(r.email);
    if (pk && phones[pk]) return;
    if (em && em.indexOf('@') > 0 && emails[em]) return;
    let leadExists = !!r.leadExists;
    let contactExists = !!r.contactExists;
    if (!leadExists && pk && kPhones[pk]) {
      leadExists = true;
      contactExists = true;
    }
    if (!leadExists && em && kEmails[em]) {
      leadExists = true;
      contactExists = true;
    }
    out.push(
      Object.assign({}, r, {
        source: 'landing',
        looksMeta: !!r.looksMeta,
        leadExists: leadExists,
        contactExists: contactExists,
        selectable: !leadExists && !contactExists && !!(r.telefono || r.email)
      })
    );
    if (pk) phones[pk] = true;
    if (em) emails[em] = true;
  });
  return out;
}

function selectFugasRows(rows) {
  const sel = {};
  (rows || []).forEach((r) => {
    if (r.selectable && !r.leadExists && !r.contactExists) sel[r.rowKey] = true;
  });
  return sel;
}

function fugasMetaStatus(instantN, insightsN) {
  if (!instantN) {
    return insightsN
      ? '0 Instant Forms con nombre/teléfono. Insights cuenta ' +
          insightsN +
          ' potenciales de pixel (no es lista de contactos).'
      : '0 Instant Forms en Meta para el período.';
  }
  const parts = ['Meta directo: ' + instantN + ' Instant Forms'];
  if (insightsN) parts.push('Insights: ' + insightsN + ' pot. (pixel)');
  return parts.join(' · ');
}

function metaGraphDiag(res) {
  if (!res) return '';
  const bits = [];
  if (res.hint) bits.push(String(res.hint));
  if (res.permissionDenied) bits.push('Sin permiso leads_retrieval');
  if (res.formsScanned != null) bits.push('formularios ' + res.formsScanned);
  if (res.pagesScanned != null) bits.push('páginas ' + res.pagesScanned);
  if (res.adsScanned != null) bits.push('ads ' + res.adsScanned);
  if (res.formsListError) bits.push('cuenta ads: ' + res.formsListError);
  if (res.error && !res.hint) bits.push(String(res.error));
  return bits.join(' · ');
}

export default function LeadsRecuperarCampanaPage() {
  const {
    period,
    setPeriod,
    periodOptions,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    awaitingCustomDates,
    range,
    periodLabel: periodLabelFromHook
  } = useLeadsData();
  const [metaCampaignKey, setMetaCampaignKey] = useState('');
  const [tab, setTab] = useState('cruce');
  const [cruceFilter, setCruceFilter] = useState('soloMeta');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [metaCampaigns, setMetaCampaigns] = useState([]);
  const [metaInsightsLeads, setMetaInsightsLeads] = useState(null);
  const [kommoLeads, setKommoLeads] = useState([]);
  const [kommoError, setKommoError] = useState('');
  const [kommoTruncated, setKommoTruncated] = useState(false);

  const fileRef = useRef(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState('');
  const [csvStatus, setCsvStatus] = useState('');
  const [csvRows, setCsvRows] = useState([]);
  const [csvSelected, setCsvSelected] = useState({});
  const [csvPushResult, setCsvPushResult] = useState(null);
  const [csvTags, setCsvTags] = useState(() => DEFAULT_CSV_KOMMO_TAGS.slice());
  const [csvTagDraft, setCsvTagDraft] = useState('');
  const [csvIncludeCampaignTag, setCsvIncludeCampaignTag] = useState(true);
  const [csvPipelines, setCsvPipelines] = useState([]);
  const [csvPipelineId, setCsvPipelineId] = useState('');
  const [csvStatusId, setCsvStatusId] = useState('');
  const [csvPipelinesBusy, setCsvPipelinesBusy] = useState(false);
  const [csvPipelinesError, setCsvPipelinesError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const forceReloadRef = useRef(false);
  const csvInstantRef = useRef([]);
  const fugasRangeRef = useRef('');
  const loadSeqRef = useRef(0);
  const abortRef = useRef(null);

  useAnalyticsRefresh(
    useCallback(() => {
      if (!hasSearched) return;
      forceReloadRef.current = true;
      setReloadTick((t) => t + 1);
    }, [hasSearched])
  );

  const from = (range && range.dateFrom) || '';
  const to = (range && range.dateTo) || '';

  const periodLabel = periodLabelFromHook || (from && to ? from + ' → ' + to : 'período');

  const csvSelectable = useMemo(
    () => csvRows.filter((r) => r.selectable !== false && (r.telefono || r.email)),
    [csvRows]
  );
  const csvSelectedCount = useMemo(
    () => csvSelectable.filter((r) => csvSelected[r.rowKey]).length,
    [csvSelectable, csvSelected]
  );
  const csvSelectedForceCount = useMemo(
    () =>
      csvSelectable.filter(
        (r) => csvSelected[r.rowKey] && (r.leadExists || r.sameMetaLeadId || r.contactExists)
      ).length,
    [csvSelectable, csvSelected]
  );
  const csvSelectedCampaignNames = useMemo(() => {
    const map = {};
    csvSelectable.forEach((r) => {
      if (!csvSelected[r.rowKey]) return;
      const n = normalizeCsvTagName(r.campaignName);
      if (n) map[n.toLowerCase()] = n;
    });
    return Object.keys(map)
      .sort()
      .map((k) => map[k]);
  }, [csvSelectable, csvSelected]);
  const csvCampaignSuggestions = useMemo(() => {
    const existing = {};
    csvTags.forEach((t) => {
      existing[normalizeCsvTagName(t).toLowerCase()] = true;
    });
    const map = {};
    csvRows.forEach((r) => {
      const n = normalizeCsvTagName(r.campaignName);
      if (!n || existing[n.toLowerCase()]) return;
      map[n.toLowerCase()] = n;
    });
    return Object.keys(map)
      .sort()
      .map((k) => map[k]);
  }, [csvRows, csvTags]);
  const csvTagsPreview = useMemo(() => {
    const out = [];
    const seen = {};
    csvTags.forEach((t) => {
      const n = normalizeCsvTagName(t);
      if (!n) return;
      const k = n.toLowerCase();
      if (seen[k]) return;
      seen[k] = true;
      out.push(n);
    });
    if (csvIncludeCampaignTag) {
      csvSelectedCampaignNames.forEach((n) => {
        const k = n.toLowerCase();
        if (seen[k]) return;
        seen[k] = true;
        out.push(n);
      });
    }
    const src = CSV_KOMMO_SOURCE_NAME;
    const sk = src.toLowerCase();
    if (!seen[sk]) out.push(src);
    return out;
  }, [csvTags, csvIncludeCampaignTag, csvSelectedCampaignNames]);

  const csvSelectedPipeline = useMemo(
    () => csvPipelines.find((p) => String(p.id) === String(csvPipelineId)) || null,
    [csvPipelines, csvPipelineId]
  );
  const csvStageOptions = useMemo(() => {
    const list = (csvSelectedPipeline && csvSelectedPipeline.statuses) || [];
    return list.slice().sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0));
  }, [csvSelectedPipeline]);
  const csvSelectedStage = useMemo(
    () => csvStageOptions.find((s) => String(s.id) === String(csvStatusId)) || null,
    [csvStageOptions, csvStatusId]
  );
  const csvStageLabel = useMemo(() => {
    const pipe = (csvSelectedPipeline && csvSelectedPipeline.name) || '';
    const st = (csvSelectedStage && csvSelectedStage.name) || '';
    if (pipe && st) return pipe + ' → ' + st;
    if (pipe) return pipe;
    return '';
  }, [csvSelectedPipeline, csvSelectedStage]);

  async function loadCsvPipelines(token) {
    setCsvPipelinesBusy(true);
    setCsvPipelinesError('');
    try {
      const res = await listMetaCsvKommoPipelines(token);
      if (res && res.success === false) {
        throw new Error(res.error || 'No se pudieron cargar embudos Kommo');
      }
      const pipes = Array.isArray(res && res.pipelines) ? res.pipelines : [];
      setCsvPipelines(pipes);
      const def = (res && res.defaults) || {};
      let pipeId = String(def.pipelineId || '');
      if (!pipeId && pipes.length) pipeId = String(pipes[0].id);
      setCsvPipelineId(pipeId);
      setCsvStatusId('');
    } catch (err) {
      setCsvPipelines([]);
      setCsvPipelineId('');
      setCsvStatusId('');
      setCsvPipelinesError((err && err.message) || String(err));
    } finally {
      setCsvPipelinesBusy(false);
    }
  }

  function onCsvPipelineChange(nextPipelineId) {
    setCsvPipelineId(nextPipelineId);
    setCsvStatusId('');
  }

  // Solo al pulsar Buscar (reloadTick). Cambiar período no dispara carga.
  useEffect(() => {
    if (!hasSearched) return;
    if (awaitingCustomDates) return;
    if (!from || !to) return;
    const seq = ++loadSeqRef.current;
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (e) {
        /* ignore */
      }
    }
    const abortCtrl = typeof AbortController === 'function' ? new AbortController() : null;
    abortRef.current = abortCtrl;
    let cancelled = false;
    const isStale = () => cancelled || seq !== loadSeqRef.current;
    const force = forceReloadRef.current;
    forceReloadRef.current = false;
    const cacheKey = fugasCacheKey(from, to);
    const rangeKey = String(from) + '|' + String(to);
    if (fugasRangeRef.current && fugasRangeRef.current !== rangeKey) {
      csvInstantRef.current = [];
    }
    fugasRangeRef.current = rangeKey;
    if (!force) {
      const hit = cacheGet(cacheKey);
      if (hit && hit.data) {
        const d = hit.data;
        csvInstantRef.current = (Array.isArray(d.csvRows) ? d.csvRows : []).filter(
          (r) => r.fromCsv && r.source !== 'landing'
        );
        setKommoLeads(Array.isArray(d.kommoLeads) ? d.kommoLeads : []);
        setKommoError(d.kommoError || '');
        setKommoTruncated(!!d.kommoTruncated);
        setCsvRows(
          (Array.isArray(d.csvRows) ? d.csvRows : []).filter((r) => r.source !== 'landing')
        );
        setCsvSelected(d.csvSelected && typeof d.csvSelected === 'object' ? d.csvSelected : {});
        setCsvStatus(
          d.csvStatus
            ? String(d.csvStatus)
            : 'Caché local · Buscar de nuevo para recargar.'
        );
        setCsvError(d.csvError || '');
        setError(d.error || '');
        setMetaCampaigns(Array.isArray(d.metaCampaigns) ? d.metaCampaigns : []);
        setMetaInsightsLeads(d.metaInsightsLeads != null ? d.metaInsightsLeads : null);
        setBusy(false);
        setCsvBusy(false);
        if (Array.isArray(d.csvRows) && d.csvRows.length) {
          getIdToken(false)
            .then((token) => loadCsvPipelines(token))
            .catch(() => {});
        }
        return undefined;
      }
    }
    (async () => {
      setBusy(true);
      setCsvBusy(true);
      setError('');
      setCsvError('');
      setCsvStatus('Cargando Meta y Kommo del período…');
      setMetaCampaignKey('');
      setMetaCampaigns([]);
      setMetaInsightsLeads(null);
      setCsvPushResult(null);
      resetCsvTagsDefault();
      const snap = {
        kommoLeads: [],
        kommoError: '',
        kommoTruncated: false,
        csvRows: [],
        csvSelected: {},
        csvStatus: '',
        csvError: '',
        metaCampaigns: [],
        metaInsightsLeads: null,
        metaDiag: '',
        metaViaApi: false,
        error: ''
      };
      try {
        const token = await getIdToken(false);
        if (isStale()) return;
        const buckets = { instant: [] };

        const publishMeta = function () {
          const instant = concatInstantRows(buckets.instant, csvInstantRef.current);
          const merged = mergeMetaContactRows(instant, [], snap.kommoLeads);
          snap.csvRows = merged;
          snap.csvSelected = selectFugasRows(merged);
          var status = fugasMetaStatus(instant.length, snap.metaInsightsLeads);
          if (snap.metaViaApi) status += ' · vía API';
          if (!instant.length && snap.metaDiag) status += ' ' + snap.metaDiag;
          snap.csvStatus = status;
          if (isStale()) return;
          setCsvRows(merged);
          setCsvSelected(snap.csvSelected);
          setCsvStatus(snap.csvStatus);
          if (instant.length) {
            getIdToken(false)
              .then((t) => loadCsvPipelines(t))
              .catch(() => {});
          }
        };

        const kommoP = fetchKommoLeadsInRange({ dateFrom: from, dateTo: to }, token)
          .then((kommoRes) => {
            if (isStale()) return;
            if (kommoRes && kommoRes.success === false) {
              snap.kommoLeads = [];
              snap.kommoError = kommoRes.error || 'No se pudieron leer leads Kommo';
              snap.kommoTruncated = false;
              setKommoLeads([]);
              setKommoError(snap.kommoError);
              setKommoTruncated(false);
              publishMeta();
              return;
            }
            snap.kommoLeads = Array.isArray(kommoRes && kommoRes.leads) ? kommoRes.leads : [];
            snap.kommoError = '';
            snap.kommoTruncated = !!(kommoRes && kommoRes.truncated);
            setKommoLeads(snap.kommoLeads);
            setKommoError('');
            setKommoTruncated(snap.kommoTruncated);
            publishMeta();
          })
          .catch((err) => {
            if (isStale()) return;
            snap.kommoLeads = [];
            snap.kommoError = (err && err.message) || String(err);
            snap.kommoTruncated = false;
            setKommoLeads([]);
            setKommoError(snap.kommoError);
            setKommoTruncated(false);
            publishMeta();
          });

        const metaP = (async () => {
          setCsvStatus('Extrayendo Instant Forms de Meta…');
          const metaRes = await fetchMetaLeadAdsContacts(
            { dateFrom: from, dateTo: to, campaignId: '', campaignName: '' },
            token,
            { signal: abortCtrl ? abortCtrl.signal : undefined }
          );
          if (isStale()) return;
          applyMetaCampaigns_(metaRes);
          if (Array.isArray(metaRes && metaRes.campaigns) && metaRes.campaigns.length) {
            snap.metaCampaigns = metaRes.campaigns;
          }
          if (metaRes && metaRes.insightsLeads != null) {
            snap.metaInsightsLeads = Number(metaRes.insightsLeads) || 0;
          }
          snap.metaDiag = metaGraphDiag(metaRes);
          snap.metaViaApi = !!(metaRes && metaRes.source === 'gicapital-api');
          if (metaRes && metaRes.permissionDenied) {
            snap.csvError =
              metaRes.hint ||
              metaRes.error ||
              'El token Meta no tiene permiso leads_retrieval. Subí el CSV de Ads Manager / Centro de leads.';
            setCsvError(snap.csvError);
            buckets.instant = [];
            publishMeta();
            return;
          }
          if (metaRes && metaRes.success === false && !csvInstantRef.current.length) {
            throw new Error(metaRes.error || 'No se pudieron extraer contactos de Meta');
          }
          const leads = Array.isArray(metaRes && metaRes.leads) ? metaRes.leads : [];
          if (!leads.length) {
            buckets.instant = [];
            publishMeta();
            return;
          }
          const matched = await matchLeadsWithKommo(leads, 'Instant Forms ' + leads.length, {
            skipApply: true
          });
          if (isStale()) return;
          buckets.instant = (Array.isArray(matched) ? matched : []).map((r) =>
            Object.assign({}, r, { source: r.source || 'lead_ads' })
          );
          publishMeta();
        })().catch((err) => {
          if (isStale()) return;
          if (err && err.name === 'AbortError') return;
          buckets.instant = [];
          snap.csvError = (err && err.message) || String(err);
          setCsvError(snap.csvError);
          publishMeta();
        });

        await Promise.all([kommoP, metaP]);
        if (!isStale()) cacheSet(cacheKey, snap);
      } catch (e) {
        if (!isStale()) {
          snap.error = (e && e.message) || String(e);
          setError(snap.error);
        }
      } finally {
        if (!isStale()) {
          setBusy(false);
          setCsvBusy(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (abortCtrl) {
        try {
          abortCtrl.abort();
        } catch (e) {
          /* ignore */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  function runFugasSearch(force) {
    if (awaitingCustomDates || !from || !to) return;
    setHasSearched(true);
    forceReloadRef.current = !!force;
    setReloadTick((t) => t + 1);
  }

  function cancelFugasSearch() {
    loadSeqRef.current += 1;
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (e) {
        /* ignore */
      }
      abortRef.current = null;
    }
    setBusy(false);
    setCsvBusy(false);
    setCsvStatus((prev) =>
      prev && /Cargando|Extrayendo|Buscando/i.test(String(prev))
        ? 'Búsqueda cancelada. Pulsá Buscar para consultar de nuevo.'
        : prev || 'Búsqueda cancelada. Pulsá Buscar para consultar de nuevo.'
    );
  }

  function applyMetaCampaigns_(metaRes) {
    if (!metaRes) return;
    if (Array.isArray(metaRes.campaigns) && metaRes.campaigns.length) {
      setMetaCampaigns(metaRes.campaigns);
    }
    if (metaRes.insightsLeads != null) setMetaInsightsLeads(Number(metaRes.insightsLeads) || 0);
  }

  const metaKeys = useMemo(() => {
    const ids = {};
    const phones = {};
    const emails = {};
    csvRows.forEach((r) => {
      const mid = String(r.metaLeadId || '').trim();
      if (mid) ids[mid] = true;
      const pk = phoneKey(r.telefono || r.phoneDigits);
      if (pk) phones[pk] = true;
      const em = emailKey(r.email);
      if (em && em.indexOf('@') > 0) emails[em] = true;
    });
    return { ids, phones, emails };
  }, [csvRows]);

  const cruceMetaAmbos = useMemo(
    () => csvRows.filter((r) => !!(r.leadExists || r.contactExists)),
    [csvRows]
  );
  const cruceSoloMeta = useMemo(
    () => csvRows.filter((r) => !r.leadExists && !r.contactExists),
    [csvRows]
  );
  const cruceSoloKommo = useMemo(
    () =>
      kommoLeads.filter((k) => {
        const mid = String(k.metaLeadId || '').trim();
        if (mid && metaKeys.ids[mid]) return false;
        const pk = phoneKey(k.telefono || k.phoneDigits);
        if (pk && metaKeys.phones[pk]) return false;
        const em = emailKey(k.email);
        if (em && metaKeys.emails[em]) return false;
        return true;
      }),
    [kommoLeads, metaKeys]
  );

  const cruceRows =
    cruceFilter === 'ambos'
      ? cruceMetaAmbos
      : cruceFilter === 'soloKommo'
        ? cruceSoloKommo
        : cruceSoloMeta;

  const kpis = useMemo(
    () => [
      {
        id: 'meta',
        label: 'Contactos Meta',
        value: csvRows.length,
        hint: csvRows.length
          ? 'Instant Forms (API + CSV)' +
            (metaInsightsLeads ? ' · insights ' + metaInsightsLeads + ' pot. pixel' : '')
          : metaInsightsLeads
            ? 'Insights ' + metaInsightsLeads + ' pot. pixel · 0 Instant Forms'
            : 'Instant Forms de Meta (API o CSV)'
      },
      {
        id: 'kommo',
        label: 'Contactos Kommo',
        value: kommoLeads.length,
        hint: kommoTruncated ? 'Lista recortada (máx. 400)' : 'Leads creados en el período'
      },
      {
        id: 'ambos',
        label: 'En ambos',
        value: cruceMetaAmbos.length,
        hint: 'Instant Form que ya está en Kommo'
      },
      {
        id: 'fugas',
        label: 'Solo Meta (fugas)',
        value: cruceSoloMeta.length,
        hint: 'Instant Form sin contacto ni lead en Kommo',
        tone: cruceSoloMeta.length > 0 ? 'bad' : undefined
      }
    ],
    [csvRows.length, kommoLeads.length, kommoTruncated, cruceMetaAmbos.length, cruceSoloMeta.length, metaInsightsLeads]
  );

  async function matchLeadsWithKommo(leads, statusPrefix, opts) {
    const skipApply = !!(opts && opts.skipApply);
    const list = Array.isArray(leads) ? leads : [];
    const token = await getIdToken(false);
    const allMatched = [];
    for (let i = 0; i < list.length; i += CSV_MATCH_BATCH) {
      const batch = list.slice(i, i + CSV_MATCH_BATCH);
      setCsvStatus(
        (statusPrefix ? statusPrefix + ' · ' : '') +
          'Cruzando ' +
          Math.min(i + batch.length, list.length) +
          ' / ' +
          list.length +
          '…'
      );
      const res = await matchMetaLeadsCsv({ leads: batch }, token);
      if (res && res.success === false) {
        throw new Error(res.error || 'Error al cruzar con Kommo');
      }
      const rows = (res && res.rows) || [];
      allMatched.push.apply(allMatched, rows);
    }
    if (!skipApply) {
      setCsvRows(allMatched);
      const sel = {};
      allMatched.forEach((r) => {
        if (r.selectable && !r.leadExists && !r.contactExists) sel[r.rowKey] = true;
      });
      setCsvSelected(sel);
      const sinLead = allMatched.filter((r) => !r.leadExists).length;
      const conLead = allMatched.filter((r) => r.leadExists).length;
      const conContact = allMatched.filter((r) => r.contactExists).length;
      const phoneHits = allMatched.filter((r) => r.phoneLiveMatch || r.matchBy === 'phone').length;
      setCsvStatus(
        list.length +
          ' contactos · ' +
          conLead +
          ' con lead · ' +
          conContact +
          ' con contacto (celular) · ' +
          phoneHits +
          ' celular encontrado · ' +
          sinLead +
          ' sin lead (podés crear).'
      );
      await loadCsvPipelines(token);
    }
    return allMatched;
  }

  async function onCsvFileChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCsvError('');
    setCsvStatus('');
    setCsvPushResult(null);
    setCsvBusy(true);
    resetCsvTagsDefault();
    try {
      const decoded = await readCsvFileText(file);
      const parsed = parseMetaLeadAdsCsv(decoded.text, { encoding: decoded.encoding });
      if (!parsed.ok) {
        setCsvError(parsed.error || 'No se pudo leer el CSV');
        return;
      }
      setCsvStatus(
        'Charset: ' +
          (decoded.encoding || 'auto') +
          ' · Cruzando ' +
          parsed.leads.length +
          ' filas con Kommo…'
      );
      const matched = await matchLeadsWithKommo(
        parsed.leads,
        'CSV Ads Manager ' + parsed.leads.length,
        { skipApply: true }
      );
      const fromFile = (Array.isArray(matched) ? matched : []).map((r) =>
        Object.assign({}, r, { source: r.source || 'lead_ads', fromCsv: true })
      );
      csvInstantRef.current = fromFile;
      const graphInstant = csvRows.filter((r) => r.source !== 'landing' && !r.fromCsv);
      const instant = concatInstantRows(graphInstant, fromFile);
      const merged = mergeMetaContactRows(instant, [], kommoLeads);
      const sel = selectFugasRows(merged);
      setCsvRows(merged);
      setCsvSelected(sel);
      setCsvStatus(
        fugasMetaStatus(instant.length, metaInsightsLeads) +
          ' · CSV: ' +
          fromFile.length +
          ' contactos Meta'
      );
      cacheSet(fugasCacheKey(from, to), {
        kommoLeads: kommoLeads,
        kommoError: kommoError,
        kommoTruncated: kommoTruncated,
        csvRows: merged,
        csvSelected: sel,
        csvStatus: 'CSV Ads Manager: ' + fromFile.length + ' Instant Forms',
        csvError: '',
        metaCampaigns: metaCampaigns,
        metaInsightsLeads: metaInsightsLeads,
        error: ''
      });
      if (merged.length) {
        const token = await getIdToken(false);
        await loadCsvPipelines(token);
      }
    } catch (err) {
      setCsvError((err && err.message) || String(err));
    } finally {
      setCsvBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function extractMetaContacts() {
    if (!from || !to) {
      setCsvError('Elegí el período (desde / hasta) antes de extraer.');
      return;
    }
    setCsvError('');
    setCsvStatus('');
    setCsvPushResult(null);
    setCsvBusy(true);
    resetCsvTagsDefault();
    try {
      const token = await getIdToken(false);
      setCsvStatus('Extrayendo Instant Forms de Meta…');
      const selectedCamp = metaCampaigns.find((c) => String(c.id) === String(metaCampaignKey));
      const res = await fetchMetaLeadAdsContacts(
        {
          dateFrom: from,
          dateTo: to,
          campaignId: metaCampaignKey || '',
          campaignName: selectedCamp ? String(selectedCamp.name || '') : ''
        },
        token
      );
      if (res && res.success === false && !csvInstantRef.current.length) {
        throw new Error(res.error || 'No se pudieron extraer contactos de Meta');
      }
      applyMetaCampaigns_(res);
      if (res && res.permissionDenied) {
        setCsvError(
          res.hint ||
            res.error ||
            'El token Meta no tiene permiso leads_retrieval. Subí el CSV de Ads Manager / Centro de leads.'
        );
      }
      const instantRaw = Array.isArray(res && res.leads) ? res.leads : [];
      let instant = [];
      if (instantRaw.length) {
        const matched = await matchLeadsWithKommo(instantRaw, 'Instant Forms ' + instantRaw.length, {
          skipApply: true
        });
        instant = (Array.isArray(matched) ? matched : []).map((r) =>
          Object.assign({}, r, { source: r.source || 'lead_ads' })
        );
      }
      const instantAll = concatInstantRows(instant, csvInstantRef.current);
      const merged = mergeMetaContactRows(instantAll, [], kommoLeads);
      setCsvRows(merged);
      setCsvSelected(selectFugasRows(merged));
      if (!res || !res.permissionDenied) setCsvError('');
      var status = fugasMetaStatus(instantAll.length, res && res.insightsLeads);
      if (res && res.source === 'gicapital-api') status += ' · vía API';
      if (!instantAll.length) {
        const diag = metaGraphDiag(res);
        if (diag) status += ' ' + diag;
      }
      setCsvStatus(status);
      if (merged.length) {
        await loadCsvPipelines(token);
      }
    } catch (err) {
      setCsvError((err && err.message) || String(err));
    } finally {
      setCsvBusy(false);
    }
  }

  function toggleCsvRow(rowKey) {
    setCsvSelected((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  }

  function selectAllCsv(mode) {
    const sel = {};
    csvRows.forEach((r) => {
      const can = r.selectable !== false && (r.telefono || r.email);
      if (!can) return;
      if (mode === 'sin_contacto') {
        if (r.leadExists || r.contactExists) return;
      } else if (mode === 'sin_lead') {
        if (r.leadExists) return;
      } else if (mode === 'existentes') {
        if (!r.leadExists && !r.contactExists) return;
      }
      // mode === 'todos' → todos los seleccionables
      sel[r.rowKey] = true;
    });
    setCsvSelected(sel);
  }

  function clearCsvSelection() {
    setCsvSelected({});
  }

  function addCsvTag(raw) {
    const name = normalizeCsvTagName(raw != null ? raw : csvTagDraft);
    if (!name) return;
    setCsvTags((prev) => {
      const key = name.toLowerCase();
      if (prev.some((t) => normalizeCsvTagName(t).toLowerCase() === key)) return prev;
      return prev.concat([name]);
    });
    setCsvTagDraft('');
  }

  function removeCsvTag(name) {
    const key = normalizeCsvTagName(name).toLowerCase();
    setCsvTags((prev) => prev.filter((t) => normalizeCsvTagName(t).toLowerCase() !== key));
  }

  function resetCsvTagsDefault() {
    setCsvTags(DEFAULT_CSV_KOMMO_TAGS.slice());
    setCsvIncludeCampaignTag(true);
    setCsvTagDraft('');
  }

  async function pushSelectedCsv() {
    const toPush = csvSelectable.filter((r) => csvSelected[r.rowKey]);
    if (!toPush.length) {
      setCsvError('Seleccioná al menos un lead sin Kommo.');
      return;
    }
    const tagsForPush = csvTags.map(normalizeCsvTagName).filter(Boolean);
    if (!tagsForPush.length && !csvIncludeCampaignTag) {
      setCsvError('Agregá al menos una etiqueta Kommo, o activá incluir campaña del CSV.');
      return;
    }
    if (!csvPipelineId || !csvStatusId) {
      setCsvError('Elegí el embudo y la etapa de Kommo. La etapa no se asume sola.');
      return;
    }
    const forceN = toPush.filter((r) => r.leadExists || r.sameMetaLeadId || r.contactExists)
      .length;
    const tagLines = (csvTagsPreview.length ? csvTagsPreview : tagsForPush).join(', ');
    if (
      !window.confirm(
        '¿Crear ' +
          toPush.length +
          ' lead(s) en Kommo?\n\nEmbudo / etapa: ' +
          (csvStageLabel || csvPipelineId + ' / ' + csvStatusId) +
          '\nFuente: ' +
          CSV_KOMMO_SOURCE_NAME +
          '\nEtiquetas: ' +
          (tagLines || '(ninguna)') +
          (csvIncludeCampaignTag && csvSelectedCampaignNames.length
            ? '\n(Incluye nombre de campaña del CSV por fila cuando aplique.)'
            : '') +
          (forceN
            ? '\n\n' +
              forceN +
              ' fila(s) ya tienen contacto/lead en Kommo: se creará un lead NUEVO (mismo celular puede ser otra campaña o alianza). El contacto se reutiliza si existe.'
            : '')
      )
    ) {
      return;
    }
    setCsvBusy(true);
    setCsvError('');
    setCsvPushResult(null);
    try {
      const token = await getIdToken(false);
      let created = 0;
      let skipped = 0;
      let failed = 0;
      let sourceWarning = '';
      let sourceOk = null;
      const results = [];
      for (let i = 0; i < toPush.length; i += CSV_PUSH_BATCH) {
        const batch = toPush.slice(i, i + CSV_PUSH_BATCH).map((r) => ({
          rowKey: r.rowKey,
          metaLeadId: r.metaLeadId,
          nombre: r.nombre,
          email: r.email,
          telefono: r.telefono,
          campaignName: r.campaignName,
          campaignId: r.campaignId,
          adName: r.adName,
          formId: r.formId,
          createdAt: r.createdAt || r.createdTime,
          forceCreate: !!(r.leadExists || r.sameMetaLeadId || r.contactExists)
        }));
        setCsvStatus(
          'Cargando en Kommo ' +
            Math.min(i + batch.length, toPush.length) +
            ' / ' +
            toPush.length +
            '…'
        );
        const res = await pushMetaLeadsToKommo(
          {
            leads: batch,
            tags: tagsForPush.length ? tagsForPush : DEFAULT_CSV_KOMMO_TAGS.slice(),
            includeCampaignTag: csvIncludeCampaignTag,
            pipelineId: csvPipelineId,
            statusId: csvStatusId,
            pipelineName:
              (csvSelectedPipeline && csvSelectedPipeline.name) || '',
            statusName: (csvSelectedStage && csvSelectedStage.name) || ''
          },
          token
        );
        if (res && res.success === false) {
          throw new Error(res.error || 'Error al crear en Kommo');
        }
        created += Number(res && res.created) || 0;
        skipped += Number(res && res.skipped) || 0;
        failed += Number(res && res.failed) || 0;
        if (res && Array.isArray(res.results)) results.push.apply(results, res.results);
        if (res && res.sourceOk === false && res.sourceWarning) {
          sourceOk = false;
          sourceWarning = String(res.sourceWarning);
        } else if (res && res.sourceOk === true) {
          sourceOk = true;
        }
      }
      setCsvPushResult({
        created,
        skipped,
        failed,
        results,
        source: CSV_KOMMO_SOURCE_NAME,
        sourceOk,
        sourceWarning
      });
      setCsvStatus(
        'Listo: ' +
          created +
          ' creados · ' +
          skipped +
          ' omitidos · ' +
          failed +
          ' con error · Fuente: ' +
          CSV_KOMMO_SOURCE_NAME +
          (sourceWarning ? ' (aviso: ver abajo)' : '') +
          '. Sincronizá Kommo para verlos en el espejo.'
      );
      if (sourceWarning) setCsvError(sourceWarning);
      // Marcar creados como en Kommo en la tabla local
      const okKeys = {};
      results.forEach((r) => {
        if (r && r.ok && (r.created || r.skipped) && r.rowKey) okKeys[r.rowKey] = r;
      });
      setCsvRows((prev) =>
        prev.map((row) => {
          const hit = okKeys[row.rowKey];
          if (!hit) return row;
          return {
            ...row,
            inKommo: true,
            leadExists: true,
            contactExists: true,
            selectable: false,
            matchBy: hit.created ? 'just_created' : row.matchBy || 'skipped',
            kommoLeadId: hit.kommoLeadId || row.kommoLeadId,
            kommoContactId: hit.kommoContactId || row.kommoContactId
          };
        })
      );
      setCsvSelected({});
    } catch (err) {
      setCsvError((err && err.message) || String(err));
    } finally {
      setCsvBusy(false);
    }
  }

  const periodBar = (
    <FilterBar label="Período">
      <DateRangeFilter
        id="fugas-meta-period"
        value={period}
        options={periodOptions}
        disabled={false}
        onChange={setPeriod}
      />
      {period === 'custom' ? (
        <>
          <label className="analytics-field">
            <span>Desde</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className="analytics-field">
            <span>Hasta</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
        </>
      ) : null}
      <button
        type="button"
        className={'analytics-btn' + (busy || csvBusy ? ' is-loading' : '')}
        disabled={awaitingCustomDates || !from || !to}
        aria-busy={busy || csvBusy ? 'true' : 'false'}
        title="Consultar Meta + Kommo del período"
        onClick={() => runFugasSearch(true)}
      >
        {busy || csvBusy ? 'Buscando…' : 'Buscar'}
      </button>
      {busy || csvBusy ? (
        <button
          type="button"
          className="analytics-btn analytics-btn-ghost"
          title="Interrumpir la búsqueda en curso"
          onClick={cancelFugasSearch}
        >
          Cancelar
        </button>
      ) : null}
    </FilterBar>
  );

  const pageHeader = (
    <PageHeader
      eyebrow="Oficial"
      title="Fugas Meta"
      subtitle={
        'Meta se lee por gicapital-api. El cruce y la carga a Kommo se preparan acá: ves el plan y tenés que autorizarlo. Nada se crea solo.'
      }
    />
  );

  if (awaitingCustomDates) {
    return (
      <div className="analytics-module fugas-meta-page">
        {pageHeader}
        {periodBar}
        <p className="analytics-empty">
          Elegí <strong>Desde</strong> y <strong>Hasta</strong>, luego pulsá <strong>Buscar</strong>.
        </p>
      </div>
    );
  }

  if (!hasSearched && !busy && !csvBusy) {
    return (
      <div className="analytics-module fugas-meta-page">
        {pageHeader}
        {periodBar}
        <p className="analytics-empty">
          Período listo ({periodLabel}). Pulsá <strong>Buscar</strong> para cruzar Meta y Kommo.
          El cruce de 30 días puede tardar; preferí 7 días si querés una vista rápida.
        </p>
      </div>
    );
  }

  function exportMetaRows() {
    downloadCsv(
      'fugas-meta-contactos-' + from + '-' + to + '.csv',
      [
        'origen',
        'metaLeadId',
        'createdAt',
        'nombre',
        'telefono',
        'email',
        'campaignName',
        'adName',
        'inKommo'
      ],
      csvRows.map((r) => ({
        origen: metaOrigenLabel(r),
        metaLeadId: r.metaLeadId,
        createdAt: r.createdAt || r.createdTime,
        nombre: r.nombre,
        telefono: r.telefono,
        email: r.email,
        campaignName: r.campaignName,
        adName: r.adName,
        inKommo: r.leadExists || r.contactExists ? 'SI' : 'NO'
      }))
    );
  }

  function exportKommoRows() {
    downloadCsv(
      'fugas-meta-kommo-' + from + '-' + to + '.csv',
      ['leadId', 'createdAt', 'name', 'telefono', 'email', 'stageName', 'campaignName', 'metaLeadId'],
      kommoLeads.map((r) => ({
        leadId: r.leadId,
        createdAt: r.createdAt,
        name: r.name,
        telefono: r.telefono,
        email: r.email,
        stageName: r.stageName,
        campaignName: r.campaignName,
        metaLeadId: r.metaLeadId
      }))
    );
  }

  const loading = busy || csvBusy;

  return (
    <div className="analytics-module fugas-meta-page">
      {pageHeader}
      {periodBar}
      <section className="analytics-section">
        <KpiGrid items={kpis} />
        {error ? <p className="analytics-error">{error}</p> : null}
        {csvError && tab !== 'meta' ? <p className="analytics-error">{csvError}</p> : null}
        {csvStatus ? <p className="analytics-meta-bar">{csvStatus}</p> : null}
        {kommoError ? <p className="analytics-error">{kommoError}</p> : null}
        {kommoTruncated ? (
          <p className="analytics-meta-bar">
            Kommo: se listan 400 leads del período (hay más en el espejo).
          </p>
        ) : null}
        {loading ? <LoadingState /> : null}
      </section>

      <nav className="leads-subnav fugas-meta-tabs" aria-label="Vistas Fugas Meta">
        <button
          type="button"
          className={'leads-subnav-link' + (tab === 'meta' ? ' is-active' : '')}
          onClick={() => setTab('meta')}
        >
          Contactos Meta ({csvRows.length})
        </button>
        <button
          type="button"
          className={'leads-subnav-link' + (tab === 'kommo' ? ' is-active' : '')}
          onClick={() => setTab('kommo')}
        >
          Contactos Kommo ({kommoLeads.length})
        </button>
        <button
          type="button"
          className={'leads-subnav-link' + (tab === 'cruce' ? ' is-active' : '')}
          onClick={() => setTab('cruce')}
        >
          Cruce ({cruceSoloMeta.length} fugas)
        </button>
      </nav>

      {tab === 'meta' ? (
        <section className="analytics-section">
          <SectionHeader
            title={'Contactos Meta (' + csvRows.length + ')'}
            note={
              'Instant Forms vía API en ' +
              periodLabel +
              '. CSV de Ads Manager queda de respaldo. El pixel no entrega nombres. Nada se carga a Kommo desde esta tabla.'
            }
          />
          <div className="fugas-meta-toolbar">
            <label className="analytics-field fugas-meta-campaign">
              <span>Campaña Meta</span>
              <select
                value={metaCampaignKey}
                disabled={loading}
                onChange={(e) => setMetaCampaignKey(e.target.value)}
              >
                <option value="">— Todas las del período —</option>
                {metaCampaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.name || c.id) +
                      ' · ' +
                      (c.leads != null ? c.leads : 0) +
                      ' pot. · $' +
                      fmtMoney(c.spend)}
                  </option>
                ))}
              </select>
            </label>
            <div className="fugas-meta-actions">
              <button
                type="button"
                className="analytics-btn"
                disabled={loading || !from || !to}
                onClick={extractMetaContacts}
              >
                {loading
                  ? 'Procesando…'
                  : metaCampaignKey
                    ? 'Extraer esta campaña'
                    : 'Volver a extraer el período'}
              </button>
              <label className={'fugas-meta-file' + (loading ? ' is-disabled' : '')}>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.txt,text/csv,text/plain"
                  disabled={loading}
                  onChange={onCsvFileChange}
                />
                Subir CSV
              </label>
              <button
                type="button"
                className="analytics-btn analytics-btn-ghost"
                disabled={!csvRows.length}
                onClick={exportMetaRows}
              >
                Exportar CSV
              </button>
            </div>
          </div>
          {csvError ? <p className="analytics-error">{csvError}</p> : null}
          {!csvRows.length && !loading ? (
            <p className="analytics-empty">
              0 Instant Forms en Meta para este período. Reintentá extraer, o subí el CSV de Ads
              Manager como respaldo.
            </p>
          ) : csvRows.length ? (
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Origen</th>
                    <th>Fecha</th>
                    <th>Nombre</th>
                    <th>Teléfono</th>
                    <th>Email</th>
                    <th>Campaña</th>
                    <th>Meta Lead ID</th>
                    <th>En Kommo</th>
                  </tr>
                </thead>
                <tbody>
                  {csvRows.map((r) => (
                    <tr key={r.rowKey || r.metaLeadId}>
                      <td>{metaOrigenLabel(r)}</td>
                      <td>{fmtDate(r.createdAt || r.createdTime)}</td>
                      <td>{r.nombre || '—'}</td>
                      <td>{r.telefono || '—'}</td>
                      <td>{r.email || '—'}</td>
                      <td>{r.campaignName || '—'}</td>
                      <td>
                        <code style={{ fontSize: 11 }}>{r.metaLeadId || '—'}</code>
                      </td>
                      <td className={r.leadExists || r.contactExists ? '' : 'is-bad'}>
                        {r.leadExists ? 'Lead' : r.contactExists ? 'Contacto' : 'No'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      {tab === 'kommo' ? (
        <section className="analytics-section">
          <SectionHeader
            title={'Contactos Kommo (' + kommoLeads.length + ')'}
            note={'Leads del espejo creados en ' + periodLabel + '.'}
          />
          <div className="fugas-meta-actions">
            <button
              type="button"
              className="analytics-btn analytics-btn-ghost"
              disabled={!kommoLeads.length}
              onClick={exportKommoRows}
            >
              Exportar CSV
            </button>
          </div>
          {!kommoLeads.length && !loading ? (
            <p className="analytics-empty">Sin leads Kommo en este período.</p>
          ) : kommoLeads.length ? (
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Lead ID</th>
                    <th>Fecha</th>
                    <th>Nombre</th>
                    <th>Teléfono</th>
                    <th>Email</th>
                    <th>Etapa</th>
                    <th>Campaña Meta</th>
                  </tr>
                </thead>
                <tbody>
                  {kommoLeads.map((r) => (
                    <tr key={r.leadId}>
                      <td>
                        {r.leadId ? (
                          <a
                            href={'https://giselladittamo.kommo.com/leads/detail/' + r.leadId}
                            target="_blank"
                            rel="noreferrer"
                          >
                            #{r.leadId}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{fmtDate(r.createdAt)}</td>
                      <td>{r.name || '—'}</td>
                      <td>{r.telefono || '—'}</td>
                      <td>{r.email || '—'}</td>
                      <td>{r.stageName || r.pipelineName || '—'}</td>
                      <td>{r.campaignName || r.campaignId || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      {tab === 'cruce' ? (
        <section className="analytics-section">
          <SectionHeader
            title="Cruce Meta × Kommo"
            note="Match por Meta Lead ID, teléfono o email. Solo Meta = Instant Form que no está en Kommo. Elegí filas, embudo y etapa; nada se crea hasta que confirmes Cargar a Kommo."
          />
          <div className="fugas-meta-seg" role="tablist" aria-label="Filtro de cruce">
            <button
              type="button"
              role="tab"
              aria-selected={cruceFilter === 'soloMeta'}
              className={'fugas-meta-seg-btn' + (cruceFilter === 'soloMeta' ? ' is-active' : '')}
              onClick={() => setCruceFilter('soloMeta')}
            >
              Solo Meta
              <span className="fugas-meta-seg-count">{cruceSoloMeta.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={cruceFilter === 'ambos'}
              className={'fugas-meta-seg-btn' + (cruceFilter === 'ambos' ? ' is-active' : '')}
              onClick={() => setCruceFilter('ambos')}
            >
              En ambos
              <span className="fugas-meta-seg-count">{cruceMetaAmbos.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={cruceFilter === 'soloKommo'}
              className={'fugas-meta-seg-btn' + (cruceFilter === 'soloKommo' ? ' is-active' : '')}
              onClick={() => setCruceFilter('soloKommo')}
            >
              Solo Kommo
              <span className="fugas-meta-seg-count">{cruceSoloKommo.length}</span>
            </button>
          </div>

          {cruceFilter === 'soloMeta' ? (
            <>
              <div className="fugas-meta-actions">
                <button
                  type="button"
                  className="analytics-btn analytics-btn-ghost"
                  disabled={loading}
                  onClick={() => selectAllCsv('sin_contacto')}
                >
                  Seleccionar sin contacto
                </button>
                <button
                  type="button"
                  className="analytics-btn analytics-btn-ghost"
                  disabled={loading}
                  onClick={clearCsvSelection}
                >
                  Limpiar selección
                </button>
                <button
                  type="button"
                  className="analytics-btn"
                  disabled={loading || !csvSelectedCount || !csvPipelineId || !csvStatusId}
                  onClick={pushSelectedCsv}
                >
                  {loading
                    ? 'Procesando…'
                    : 'Cargar ' +
                      csvSelectedCount +
                      ' a Kommo' +
                      (csvSelectedForceCount
                        ? ' (' + csvSelectedForceCount + ' ya en Kommo → lead nuevo)'
                        : '')}
                </button>
              </div>
              {csvRows.length ? (
                <div className="analytics-csv-tags-panel">
                  <div className="analytics-csv-tags-head">
                    <strong>Destino en Kommo</strong>
                    <span className="analytics-muted">Embudo y etapa para los leads seleccionados.</span>
                  </div>
                  {csvPipelinesBusy ? <p className="analytics-meta-bar">Cargando embudos de Kommo…</p> : null}
                  {csvPipelinesError ? <p className="analytics-error">{csvPipelinesError}</p> : null}
                  <div className="analytics-csv-stage-row">
                    <label className="analytics-field">
                      <span>Embudo</span>
                      <select
                        className="analytics-select"
                        value={csvPipelineId}
                        disabled={loading || csvPipelinesBusy || !csvPipelines.length}
                        onChange={(e) => onCsvPipelineChange(e.target.value)}
                      >
                        {!csvPipelines.length ? <option value="">— Sin embudos —</option> : null}
                        {csvPipelines.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="analytics-field">
                      <span>Etapa</span>
                      <select
                        className="analytics-select"
                        value={csvStatusId}
                        required
                        disabled={loading || csvPipelinesBusy || !csvStageOptions.length}
                        onChange={(e) => setCsvStatusId(e.target.value)}
                      >
                        <option value="">
                          {csvStageOptions.length ? '— Elegí etapa —' : '— Elegí embudo —'}
                        </option>
                        {csvStageOptions.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                            {s.isClosed ? ' (cerrada)' : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {csvSelectedStage ? (
                    <p className="analytics-meta-bar">
                      Los leads se crearán en: <strong>{csvStageLabel}</strong>
                      {' · '}
                      Fuente: <strong>{CSV_KOMMO_SOURCE_NAME}</strong>
                    </p>
                  ) : csvPipelineId && csvStageOptions.length ? (
                    <p className="analytics-meta-bar">
                      Elegí la etapa. No hay una por defecto, para no cargar en el lugar equivocado.
                    </p>
                  ) : null}
                  <div className="analytics-csv-tags-head">
                    <strong>Etiquetas</strong>
                  </div>
                  <div className="analytics-csv-tags-list" aria-label="Etiquetas base">
                    <span className="analytics-csv-tag is-fixed">{CSV_KOMMO_SOURCE_NAME}</span>
                    {csvTags
                      .filter(
                        (t) =>
                          normalizeCsvTagName(t).toLowerCase() !== CSV_KOMMO_SOURCE_NAME.toLowerCase()
                      )
                      .map((t) => (
                        <span key={t} className="analytics-csv-tag">
                          <span>{t}</span>
                          <button
                            type="button"
                            className="analytics-csv-tag-remove"
                            disabled={loading}
                            aria-label={'Quitar etiqueta ' + t}
                            onClick={() => removeCsvTag(t)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                  </div>
                  <div className="analytics-csv-tags-add">
                    <input
                      type="text"
                      className="analytics-input"
                      value={csvTagDraft}
                      disabled={loading}
                      maxLength={80}
                      placeholder="Nueva etiqueta…"
                      onChange={(e) => setCsvTagDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCsvTag();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="analytics-btn analytics-btn-ghost"
                      disabled={loading || !normalizeCsvTagName(csvTagDraft)}
                      onClick={() => addCsvTag()}
                    >
                      Agregar
                    </button>
                  </div>
                  {csvCampaignSuggestions.length ? (
                    <div className="analytics-csv-tags-suggest">
                      <span className="analytics-muted">Sugeridas:</span>
                      {csvCampaignSuggestions.slice(0, 8).map((c) => (
                        <button
                          key={c}
                          type="button"
                          className="analytics-btn analytics-btn-ghost analytics-csv-tag-suggest"
                          disabled={loading}
                          onClick={() => addCsvTag(c)}
                        >
                          + {c}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <label className="analytics-csv-tags-check">
                    <input
                      type="checkbox"
                      checked={csvIncludeCampaignTag}
                      disabled={loading}
                      onChange={(e) => setCsvIncludeCampaignTag(e.target.checked)}
                    />
                    Incluir nombre de campaña como etiqueta
                  </label>
                </div>
              ) : null}
              {csvPushResult ? (
                <p className="analytics-meta-bar">
                  Resultado push: {csvPushResult.created} creados · {csvPushResult.skipped} omitidos ·{' '}
                  {csvPushResult.failed} error
                </p>
              ) : null}
            </>
          ) : null}

          {!cruceRows.length && !loading ? (
            <p className="analytics-empty">
              {cruceFilter === 'soloMeta'
                ? 'No hay fugas: todos los Instant Forms matchearon en Kommo, o todavía no hay extract de Meta.'
                : cruceFilter === 'ambos'
                  ? 'Nadie aparece en ambos lados todavía.'
                  : 'No hay leads Kommo fuera del extract de Meta.'}
            </p>
          ) : cruceFilter === 'soloKommo' ? (
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Lead ID</th>
                    <th>Fecha</th>
                    <th>Nombre</th>
                    <th>Teléfono</th>
                    <th>Email</th>
                    <th>Etapa</th>
                    <th>Campaña</th>
                  </tr>
                </thead>
                <tbody>
                  {cruceSoloKommo.map((r) => (
                    <tr key={r.leadId}>
                      <td>
                        {r.leadId ? (
                          <a
                            href={'https://giselladittamo.kommo.com/leads/detail/' + r.leadId}
                            target="_blank"
                            rel="noreferrer"
                          >
                            #{r.leadId}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{fmtDate(r.createdAt)}</td>
                      <td>{r.name || '—'}</td>
                      <td>{r.telefono || '—'}</td>
                      <td>{r.email || '—'}</td>
                      <td>{r.stageName || r.pipelineName || '—'}</td>
                      <td>{r.campaignName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    {cruceFilter === 'soloMeta' ? <th /> : null}
                    <th>Origen</th>
                    <th>Nombre</th>
                    <th>Teléfono</th>
                    <th>Email</th>
                    <th>Campaña</th>
                    <th>Meta Lead ID</th>
                    <th>Kommo</th>
                  </tr>
                </thead>
                <tbody>
                  {cruceRows.map((r) => {
                    const canSelect =
                      cruceFilter === 'soloMeta' &&
                      r.selectable !== false &&
                      !!(r.telefono || r.email);
                    return (
                      <tr key={r.rowKey || r.metaLeadId}>
                        {cruceFilter === 'soloMeta' ? (
                          <td>
                            {canSelect ? (
                              <input
                                type="checkbox"
                                checked={!!csvSelected[r.rowKey]}
                                disabled={loading}
                                onChange={() => toggleCsvRow(r.rowKey)}
                              />
                            ) : (
                              '—'
                            )}
                          </td>
                        ) : null}
                        <td>{metaOrigenLabel(r)}</td>
                        <td>{r.nombre || '—'}</td>
                        <td>{r.telefono || '—'}</td>
                        <td>{r.email || '—'}</td>
                        <td>{r.campaignName || '—'}</td>
                        <td>
                          <code style={{ fontSize: 11 }}>{r.metaLeadId || '—'}</code>
                        </td>
                        <td>
                          {r.kommoLeadId ? (
                            <a
                              href={
                                'https://giselladittamo.kommo.com/leads/detail/' + r.kommoLeadId
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              Lead #{r.kommoLeadId}
                            </a>
                          ) : r.kommoContactId ? (
                            <a
                              href={
                                'https://giselladittamo.kommo.com/contacts/detail/' +
                                r.kommoContactId
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              Contacto #{r.kommoContactId}
                            </a>
                          ) : (
                            r.matchBy || '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
